/**
 * Browser-side parser for Sigma-Aldrich (Merck) order confirmation CSV exports.
 * Layout: metadata rows, blank row, then item table (not a single header-first CSV).
 */

import { parse } from 'csv-parse/browser/esm/sync';
import {
  SIGMA_MATERIAH_SUPPLIER_PRIMARY,
  SIGMA_ORDER_CONFIRMATION_BRAND,
} from '../../config/orderConfirmationFormats';
import { logError, logInfo, logWarn } from '../../config_and_helpers/logger';
import { logOrderConfirmationExtraction } from './logOrderConfirmationExtraction';

const LOG_CONTEXT = 'sigmaAldrichOrderConfirmationCsvParser';

export const SIGMA_ORDER_CONFIRMATION_FORMAT_ID =
  'sigma_aldrich_order_confirmation_csv';
export const SIGMA_PARSER_VERSION = 'order-confirmation-extractor-v1';

const EXTRACT_ERROR =
  'Could not extract order confirmation data from this CSV.';

const RAW_TEXT_MAX = 8000;

/**
 * @param {*} value
 * @returns {string|null}
 */
function clean(value) {
  if (value === undefined || value === null) return null;
  const cleaned = String(value)
    .replace(/^\uFEFF/, '')
    .trim();
  return cleaned === '' ? null : cleaned;
}

/**
 * @param {*} value
 * @returns {number|null}
 */
function parseNumber(value) {
  const cleaned = clean(value);
  if (!cleaned) return null;
  const number = Number(cleaned.replace(/[^\d.-]/g, ''));
  return Number.isFinite(number) ? number : null;
}

/**
 * @param {string|null} header
 * @returns {string|null}
 */
function inferCurrencyFromHeader(header) {
  if (!header) return null;
  const match = header.match(/\b(ILS|USD|EUR)\b/i);
  return match ? match[1].toUpperCase() : null;
}

/**
 * @param {number|null|undefined} value
 * @returns {number|null}
 */
function roundMoney(value) {
  if (value === null || value === undefined) return null;
  return Math.round(value * 100) / 100;
}

/**
 * @param {(string|null)[][]} rows
 * @returns {{
 *   orderNumber: string|null,
 *   orderDate: string|null,
 *   currency: string
 * }}
 */
function extractMetadata(rows) {
  const result = {
    orderNumber: null,
    orderDate: null,
    currency: 'ILS',
  };

  for (const row of rows) {
    const key = clean(row[0]);
    const value = clean(row[1]);
    if (!key) continue;

    if (/Sigma-Aldrich Order/i.test(key)) {
      result.orderNumber = value;
    }
    if (/Order Date/i.test(key)) {
      result.orderDate = value;
    }
  }

  return result;
}

/**
 * @param {Record<string, string|null>} mapped
 * @param {(string|null)[]} headers
 * @param {(string|null)[]} row
 * @returns {Record<string, string|null>}
 */
function mapRawRow(headers, row) {
  const mapped = {};
  headers.forEach((header, index) => {
    if (header) {
      mapped[header] = clean(row[index]);
    }
  });
  return mapped;
}

/**
 * @param {(string|null)[]} headers
 * @param {(string|null)[][]} rows
 * @returns {import('../../types/orderConfirmationExtractionTypes').ExtractedOrderItem[]}
 */
function extractItems(headers, rows) {
  const lineTotalHeader = headers.find(header => header && /^Line Total/i.test(header));

  const currency = inferCurrencyFromHeader(lineTotalHeader ?? '') ?? 'ILS';

  return rows.map(row => {
    const get = headerName => {
      const index = headers.findIndex(header => header === headerName);
      return index >= 0 ? clean(row[index]) : null;
    };

    const lineTotal = parseNumber(
      lineTotalHeader ? row[headers.indexOf(lineTotalHeader)] : null
    );

    return {
      productNumber: get('Product Number'),
      description: get('Description'),
      quantity: parseNumber(get('Order Qty')),
      lineTotal,
      currency,
      rawRow: mapRawRow(headers, row),
    };
  });
}

/**
 * @param {import('../../types/orderConfirmationExtractionTypes').ExtractedOrderItem[]} items
 * @returns {import('../../types/orderConfirmationExtractionTypes').ExtractionWarning[]}
 */
function buildWarnings(items) {
  /** @type {import('../../types/orderConfirmationExtractionTypes').ExtractionWarning[]} */
  const warnings = [];

  if (items.some(item => !item.description)) {
    warnings.push({
      field: 'items.description',
      message: 'Item descriptions are empty in this CSV.',
      severity: 'info',
    });
  }

  if (items.some(item => !item.productNumber)) {
    warnings.push({
      field: 'items.productNumber',
      message: 'One or more item rows are missing product numbers.',
      severity: 'warning',
    });
  }

  return warnings;
}

/**
 * Core parse: metadata section then item table (csv-parse with relax_column_count).
 *
 * @param {string} csvText
 * @param {string|null} [sourceFileName]
 * @returns {import('../../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationData}
 */
export function parseSigmaOrderConfirmationCsvCore(csvText, sourceFileName = null) {
  const rows = parse(csvText, {
    bom: true,
    skip_empty_lines: false,
    relax_column_count: true,
    trim: true,
  });

  const normalizedRows = rows.map(row => row.map(clean));

  const headerRowIndex = normalizedRows.findIndex(
    row =>
      row.some(cell => cell === 'Line Number') &&
      row.some(cell => cell === 'Product Number')
  );

  if (headerRowIndex === -1) {
    throw new Error('Could not find Sigma order item table header');
  }

  const metadataRows = normalizedRows.slice(0, headerRowIndex);
  const tableHeader = normalizedRows[headerRowIndex];
  const itemRows = normalizedRows
    .slice(headerRowIndex + 1)
    .filter(row => row.some(Boolean));

  const metadata = extractMetadata(metadataRows);
  const items = extractItems(tableHeader, itemRows);

  logInfo(LOG_CONTEXT, 'parse layout', {
    sourceFileName,
    totalRows: normalizedRows.length,
    headerRowIndex,
    metadataRowCount: metadataRows.length,
    itemRowCount: itemRows.length,
    tableHeaders: tableHeader.filter(Boolean),
    metadata,
  });

  const totalPrice = roundMoney(
    items.reduce((sum, item) => sum + (item.lineTotal ?? 0), 0)
  );

  const warnings = buildWarnings(items);

  const trimmed = String(csvText ?? '').trim();

  return {
    supplier: {
      name: SIGMA_ORDER_CONFIRMATION_BRAND,
      sourceType: 'order_confirmation_csv',
      recognizedBrand: SIGMA_ORDER_CONFIRMATION_BRAND,
      materiahSupplierHint: SIGMA_MATERIAH_SUPPLIER_PRIMARY,
    },
    order: {
      orderNumber: metadata.orderNumber,
      orderDate: metadata.orderDate,
      currency: metadata.currency ?? 'ILS',
    },
    items,
    totals: {
      itemsSubtotal: totalPrice,
    },
    warnings,
    meta: {
      sourceFileName: sourceFileName || undefined,
      parserFormat: SIGMA_ORDER_CONFIRMATION_FORMAT_ID,
      parserVersion: SIGMA_PARSER_VERSION,
      rowCount: items.length,
      rawText:
        trimmed.length > RAW_TEXT_MAX
          ? `${trimmed.slice(0, RAW_TEXT_MAX)}…`
          : trimmed,
    },
  };
}

/**
 * Parse Sigma-Aldrich order confirmation CSV text (public API for review flow).
 *
 * @param {string} csvText
 * @param {string} [sourceFileName]
 * @returns {import('../../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationResponse}
 */
export function parseSigmaAldrichOrderConfirmationCsv(csvText, sourceFileName = '') {
  const trimmed = String(csvText ?? '').trim();
  if (!trimmed) {
    logWarn(LOG_CONTEXT, 'empty csv text', { sourceFileName });
    return { success: false, error: EXTRACT_ERROR, message: EXTRACT_ERROR };
  }

  try {
    const data = parseSigmaOrderConfirmationCsvCore(trimmed, sourceFileName || null);

    if (!data.order.orderNumber || data.items.length === 0) {
      logError(LOG_CONTEXT, 'missing required extracted fields', {
        sourceFileName,
        orderNumber: data.order.orderNumber,
        itemCount: data.items.length,
      });
      return { success: false, error: EXTRACT_ERROR, message: EXTRACT_ERROR };
    }

    const validItems = data.items.filter(item => item.productNumber);
    if (validItems.length === 0) {
      logError(LOG_CONTEXT, 'no items with product numbers', { sourceFileName });
      return { success: false, error: EXTRACT_ERROR, message: EXTRACT_ERROR };
    }

    logOrderConfirmationExtraction(LOG_CONTEXT, 'parse success — full extracted payload', data, {
      sourceFileName,
      materiahSupplierHint: data.supplier?.materiahSupplierHint,
    });

    return { success: true, data };
  } catch (error) {
    logError(LOG_CONTEXT, 'parse failed', {
      sourceFileName,
      message: error?.message,
    });
    return { success: false, error: EXTRACT_ERROR, message: EXTRACT_ERROR };
  }
}
